/**
 * buildWebBleTicket.ts — Construye los bytes ESC/POS de un ticket de pedido a
 * partir de la SHAPE de orden del panel (`OrderDetail`).
 *
 * Puro y sin dependencias de browser: sólo usa el encoder {@link EscPos} y los
 * mismos mapas de etiquetas / helpers de formato que el ticket Electron
 * (`pages/Orders/utils/electronTicket/buildTicket.ts`). El CONTENIDO replica al
 * ticket Electron salvo lo diferido:
 *
 *   - `ticketType: 'normal'`     -> ticket completo (productos, extras, totales,
 *                                   pago, domicilio, footer del comercio).
 *   - `ticketType: 'simplified'` -> comanda de cocina SIN precios.
 *
 * DIFERIDO v1 (ver TODOs abajo): logo/imagen (raster), QR y código de barras.
 * El ticket Electron renderiza el logo como `type: 'image'`; aquí se omite a
 * propósito porque el encoder de bytes mínimo no soporta raster y no se pueden
 * añadir dependencias nuevas.
 */

import { OrderDetail, ProductExtra, ProductOrder } from '@interfaces/OrderTypes';
import { DELIVERY_MAP, ORDER_PAYMENT_MAP, PRINT_TEXT_SIZE_MAP, TICKET_TYPE_MAP } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import { normalizePaymentMethod } from '@pages/Orders/utils/paymentUtils';
import { EscPos, EscPosSize } from './escpos';

export type WebBleTicketType = keyof typeof TICKET_TYPE_MAP; // 'normal' | 'simplified'

export type WebBleTicketOptions = {
  /** Tipo de ticket: completo con precios o comanda de cocina simplificada. */
  ticketType: WebBleTicketType;
  /** Tamaño de texto del cuerpo (mapea a doble-alto en encabezados). */
  textSize?: keyof typeof PRINT_TEXT_SIZE_MAP;
  /** Datos del comercio para el footer (sólo ticket normal). */
  commerceName?: string;
  commerceAddress?: string;
  /** Etiqueta personalizada para el campo de referencias de dirección. */
  referencesLabel?: string;
  /** Flags de visibilidad — paridad con el ticket Electron. */
  showClientPhone?: boolean;
  /** Imprimir el NOMBRE del comercio. Aplica a caja Y cocina. Default true. */
  showCommerceName?: boolean;
  /** Imprimir la DIRECCIÓN del comercio (solo caja). Default true. */
  showCommerceAddress?: boolean;
  /** Líneas en blanco antes del corte (papel para arrancar el ticket). */
  feedBeforeCut?: number;
  /** Ancho del rollo en mm — define el ancho de las líneas separadoras. */
  pageWidth?: 58 | 80;
  /** Si es false, omite las líneas separadoras decorativas. */
  decoration?: boolean;
  /** Comando ESC/POS raster del logo (GS v 0) ya resuelto; se imprime arriba. */
  logoRaster?: Uint8Array | null;
};

// Caracteres por línea según el ancho del rollo (aprox. fuente A: 32 @58mm, 48 @80mm).
// Se usa para el WRAP de texto/columnas (NO para el separador). Exportado para que el
// ESPEJO de texto (textLayout.ts) maquete con el MISMO ancho de columna que la impresión.
export const bodyWidth = (options: WebBleTicketOptions): number => (options.pageWidth === 58 ? 32 : 48);

// Ancho CONSERVADOR del separador en columnas (columnas − 1). Imprimir el ancho EXACTO
// de columna provoca auto-wrap en muchas térmicas → el separador sale como línea doble.
// Bajamos a columnas−1 (58mm→31, 80mm→47) para dejar margen seguro. Exportado para que
// el preview use EXACTAMENTE el mismo valor que la impresión y no diverja.
export const separatorCols = (pageWidth?: 58 | 80): number => (pageWidth === 58 ? 31 : 47);

// Línea separadora con ancho conservador. Vacía si las decoraciones están apagadas.
const sepLine = (options: WebBleTicketOptions): string =>
  options.decoration === false ? '' : '-'.repeat(separatorCols(options.pageWidth));

const writeSep = (enc: EscPos, options: WebBleTicketOptions): void => {
  const line = sepLine(options);
  if (line) enc.line(line);
};

// Fila "etiqueta ........ valor": valor alineado a la derecha en una sola línea si
// cabe; si no, etiqueta arriba y valor a la derecha debajo.
const row = (enc: EscPos, left: string, right: string, width: number): void => {
  if (left.length + right.length + 1 <= width) {
    enc.line(left + ' '.repeat(width - left.length - right.length) + right);
  } else {
    enc.line(left);
    enc.align(2).line(right).align(0);
  }
};

// Imprime el logo (raster) centrado si está disponible.
const writeLogo = (enc: EscPos, options: WebBleTicketOptions): void => {
  if (!options.logoRaster) return;
  enc.align(1).raw(options.logoRaster).feed(1).align(0);
};

// Exportado para que el ESPEJO de texto (textLayout.ts) use literalmente el mismo
// disclaimer que la impresión y no pueda divergir en contenido.
export const LEGAL_DISCLAIMER =
  'Este formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son validos hasta la fecha indicada.';

// Mapea el tamaño de texto seleccionado al modo de tamaño ESC/POS para los
// encabezados. Las térmicas no tienen escala de px; usamos doble-alto+ancho
// para los títulos en tamaños grandes y normal en los pequeños.
export function titleSize(textSize: keyof typeof PRINT_TEXT_SIZE_MAP | undefined): EscPosSize {
  switch (textSize) {
    case 'large':
    case 'xxl':
      return 3; // doble alto + ancho
    case 'medium':
      return 1; // doble alto
    default:
      return 0; // normal (xxs / small / undefined)
  }
}

// Tamaño del CUERPO del ticket (no solo el título), para que "Tamaño del texto" afecte
// TODO el ticket en modo texto. Se usa SOLO doble-ALTO (mode 1), nunca doble-ancho: el
// ancho de carácter se mantiene, así que el conteo de columnas de `row`/`sepLine` (32/48)
// sigue siendo válido y las líneas no se desbordan. Las térmicas no tienen escala de px,
// por eso solo hay 2 pasos reales (normal / doble-alto). Para escala fiel por elemento el
// usuario elige el MODO IMAGEN ('Como en la app'), que sí soporta los 5 niveles.
export function bodySize(textSize: keyof typeof PRINT_TEXT_SIZE_MAP | undefined): EscPosSize {
  switch (textSize) {
    case 'large':
    case 'xxl':
      return 1; // doble alto en todo el cuerpo
    default:
      return 0; // normal (xxs / small / medium / undefined)
  }
}

// Etiqueta del método de pago, replicando la lógica del ticket Electron.
export function paymentLabel(order: OrderDetail): string {
  let label: string = ORDER_PAYMENT_MAP[order.pedido.metodo_pago]?.label || order.pedido.metodo_pago;
  if (normalizePaymentMethod(order.pedido.metodo_pago) === ORDER_PAYMENT_MAP.toConfirm.key) {
    label = order.pedido.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en linea';
  }
  return label;
}

// Renderiza extras de un producto (con precios).
function writeExtras(enc: EscPos, extras: ProductExtra[]): void {
  for (const extra of extras) {
    enc.line(`  ${extra.cantidad} x ${extra.nombre}  ${formatToCurrency(extra.precio)}`);
  }
}

// Renderiza extras de un producto (sin precios, comanda de cocina).
function writeExtrasSimplified(enc: EscPos, extras: ProductExtra[]): void {
  for (const extra of extras) {
    enc.line(`  + ${extra.cantidad} x ${extra.nombre}`);
  }
}

// Productos con precios (ticket normal).
function writeProducts(enc: EscPos, products: ProductOrder[], options: WebBleTicketOptions): void {
  const width = bodyWidth(options);
  for (const product of products) {
    const name = `${product.cantidad} x ${product.nombre} - ${product.presentacion}`;
    enc.bold(true);
    row(enc, name, formatToCurrency(product.subtotal), width);
    enc.bold(false);
    if (product.instrucciones) {
      enc.line(`  ${product.instrucciones}`);
    }
    writeExtras(enc, product.extras);
    writeSep(enc, options);
  }
}

// Productos sin precios (comanda de cocina).
function writeProductsSimplified(enc: EscPos, products: ProductOrder[], options: WebBleTicketOptions): void {
  for (const product of products) {
    enc.bold(true).line(`${product.cantidad} x ${product.nombre} - ${product.presentacion}`).bold(false);
    if (product.instrucciones) {
      enc.line(`  Instrucciones: ${product.instrucciones}`);
    }
    writeExtrasSimplified(enc, product.extras);
    writeSep(enc, options);
  }
}

// Total que se IMPRIME ("Total a pagar"), calculado localmente igual que el ticket
// Electron (getOrderFooter): subtotal - descuento + propina + envío. El "Cambio" debe
// calcularse contra ESTE mismo total para ser coherente con lo impreso — no contra
// `pedido.total` del servidor, que puede diferir (redondeo / fees) y dar un cambio
// inconsistente con el total mostrado.
export function computeOrderTotal(pedido: OrderDetail['pedido']): number {
  return (pedido.subtotal || 0) - (pedido.coupon_amount || 0) + (pedido.propina || 0) + (pedido.costo_envio || 0);
}

// Bloque de totales del ticket normal (replica `getOrderFooter`).
function writeFooterTotals(enc: EscPos, order: OrderDetail): void {
  const { pedido } = order;
  enc.align(2);
  enc.line(`Subtotal: ${formatToCurrency(pedido.subtotal || 0)}`);
  if (pedido.coupon_amount) {
    enc.line(`Descuento: -${formatToCurrency(pedido.coupon_amount)}`);
  }
  if (pedido.propina) {
    enc.line(`Propina: ${formatToCurrency(pedido.propina)}`);
  }
  // Solo en delivery (igual que pdfMake createOrderTotalsSection).
  if (pedido.metodo_entrega === 'delivery') {
    enc.line(`Costo de envio: ${formatToCurrency(pedido.costo_envio || 0)}`);
  }
  const total = computeOrderTotal(pedido);
  enc
    .bold(true)
    .line(`Total a pagar: ${formatToCurrency(total)}`)
    .bold(false);
  enc.align(0);
}

// Bloque de domicilio de entrega (sólo delivery, ticket normal).
function writeDeliveryAddress(enc: EscPos, order: OrderDetail, referencesLabel: string): void {
  const { pedido } = order;
  if (pedido.metodo_entrega !== 'delivery') return;
  enc.bold(true).line('Domicilio de entrega:').bold(false);
  if (pedido.colonia) enc.line(`Zona o Colonia: ${pedido.colonia}`);
  if (pedido.calle) enc.line(`Calle o Mza: ${pedido.calle}`);
  if (pedido.numero_exterior) enc.line(`Numero Exterior: ${pedido.numero_exterior}`);
  if (pedido.numero_interior) enc.line(`Numero interior: ${pedido.numero_interior}`);
  if (pedido.direccion_completa) enc.line(`Direccion: ${pedido.direccion_completa}`);
  if (pedido.referencias) enc.line(`${referencesLabel}: ${pedido.referencias}`);
}

// Ticket completo con precios y totales.
function buildNormalTicket(order: OrderDetail, options: WebBleTicketOptions): EscPos {
  const { pedido } = order;
  const enc = new EscPos();
  const tSize = titleSize(options.textSize);
  const bSize = bodySize(options.textSize);
  const referencesLabel = options.referencesLabel || 'Referencias';
  const showClientPhone = options.showClientPhone !== false;
  const showCommerceName = options.showCommerceName !== false;
  const showCommerceAddress = options.showCommerceAddress !== false;

  writeLogo(enc, options);

  // Bloque cliente CENTRADO (igual que pdfMake createClientInfoSection y el modo imagen):
  // título, datos del cliente y "Detalle del pedido:" van centrados; el detalle de
  // productos vuelve a la izquierda más abajo.
  enc.align(1);
  // El título usa su propio tamaño; al terminar se restaura el tamaño del CUERPO (no 0),
  // para que el resto del ticket también escale con "Tamaño del texto".
  enc.size(tSize).bold(true).line('Ticket pedido').line(`No. ${pedido.folio}`).bold(false).size(bSize);
  enc.feed(1);

  if (pedido.nombre) {
    enc.bold(true).line('Cliente:').line(pedido.nombre).bold(false);
  }
  if (showClientPhone && pedido.numero_celular) {
    const code = pedido.country_code ? `+${pedido.country_code} ` : '';
    enc.line(`Celular: ${code}${pedido.numero_celular}`);
  }
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    enc.line(`Mesa: ${tableNumber}`);
  }
  enc.feed(1);

  enc.bold(true).line('Detalle del pedido:').bold(false);
  enc.align(0);
  writeSep(enc, options);
  writeProducts(enc, order.productos, options);
  writeFooterTotals(enc, order);
  enc.feed(1);

  if (pedido.comentario) {
    enc.bold(true).line(`Indicaciones: ${pedido.comentario}`).bold(false);
  }
  enc.line(`Metodo de entrega: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`);
  enc.line(`Tipo de pago: ${paymentLabel(order)}`);

  const isCash = normalizePaymentMethod(pedido.metodo_pago) === ORDER_PAYMENT_MAP.efectivo.key;
  if (pedido.cliente_paga_con && isCash) {
    const paidWith = parseFloat(pedido.cliente_paga_con || '0');
    enc.line(`Cliente paga con: ${formatToCurrency(paidWith)}`);
    enc.line(`Cambio: ${formatToCurrency(paidWith - computeOrderTotal(pedido))}`);
  }

  writeDeliveryAddress(enc, order, referencesLabel);

  writeSep(enc, options);
  enc.align(1);
  // Nombre gateado por showCommerceName; dirección por showCommerceAddress (independientes).
  if (showCommerceName && options.commerceName) enc.bold(true).line(options.commerceName).bold(false);
  if (showCommerceAddress && options.commerceAddress) enc.line(options.commerceAddress);
  enc.line(`Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`);
  enc.line(LEGAL_DISCLAIMER);

  enc.feed(options.feedBeforeCut ?? 3).cut();
  return enc;
}

// Comanda de cocina simplificada (sin precios).
function buildSimplifiedTicket(order: OrderDetail, options: WebBleTicketOptions): EscPos {
  const { pedido } = order;
  const enc = new EscPos();
  const tSize = titleSize(options.textSize);
  const bSize = bodySize(options.textSize);

  writeLogo(enc, options);

  enc.align(1);
  enc.size(tSize).bold(true).line('Ticket de Cocina').line(`No. ${pedido.folio}`).bold(false).size(bSize);
  enc.line(`Metodo: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`);
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    enc.line(`Mesa: ${tableNumber}`);
  }
  enc.feed(1);

  // "Productos:" CENTRADO (igual que el preview/imagen y que "Detalle del pedido:" del
  // ticket normal); el detalle de productos vuelve a la izquierda.
  enc.bold(true).line('Productos:').bold(false);
  enc.align(0);
  writeProductsSimplified(enc, order.productos, options);

  if (pedido.comentario) {
    enc.bold(true).line(`Indicaciones: ${pedido.comentario}`).bold(false);
  }

  writeSep(enc, options);
  enc.align(1);
  // Nombre del comercio: gateado por showCommerceName (aplica también a la comanda).
  if (options.showCommerceName !== false && options.commerceName) {
    enc.bold(true).line(options.commerceName).bold(false);
  }
  enc.line(`Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`);

  enc.feed(options.feedBeforeCut ?? 3).cut();
  return enc;
}

// Construye los bytes ESC/POS del ticket para imprimir por Web Bluetooth.
// Devuelve un `Uint8Array` listo para escribir a la característica BLE.
export function buildWebBleTicket(order: OrderDetail, options: WebBleTicketOptions): Uint8Array {
  const enc =
    options.ticketType === TICKET_TYPE_MAP.simplified.key
      ? buildSimplifiedTicket(order, options)
      : buildNormalTicket(order, options);
  return enc.encode();
}
